"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findGolfAssignment = void 0;
const dayjs = require("dayjs");
const isSameOrBefore = require("dayjs/plugin/isSameOrBefore");
dayjs.extend(isSameOrBefore);
const isSameOrAfter = require("dayjs/plugin/isSameOrAfter");
dayjs.extend(isSameOrAfter);
const models_1 = require("../../models");
const course_1 = require("../course");
const orders_1 = require("../orders");
function findGolfAssignment(requiredCourses, rate_matrix, formData, preferredOrder = []) {
    // Calculate duration once
    const duration = formData?.trip?.duration_days_nights || 0;
    const isGolfHotel = formData?.trip?.type === models_1.TripType.GOLF_HOTEL;
    // Get optimum days distribution
    const optimumDays = (0, course_1.getGolfDayDistribution)(requiredCourses.length, isGolfHotel ? duration : duration - 1) || [true];
    // Pre-process availability data once
    const availability = rate_matrix.map((r) => ({
        date: r.date,
        courses: r.courses.map((c) => {
            const config = c.config;
            return {
                course_id: config.course.id,
                config,
                rate: (0, orders_1.getOrderLineTotalByType)(config, "GOLF"),
                rate_message: c.rate_message,
            };
        }),
    }));
    // Validation: number of true values in optimumDays must equal required courses.
    const trueCount = optimumDays.filter((day) => day).length;
    if (trueCount !== requiredCourses.length) {
        throw new Error(`Number of true values in optimumDays (${trueCount}) must match the number of required courses (${requiredCourses.length})`);
    }
    // First attempt: Try optimum days
    const optimumAssignments = findAssignmentsForDays(requiredCourses, availability, optimumDays, true);
    if (optimumAssignments.length > 0) {
        const optimalOptions = optimumAssignments.map((assignment) => ({
            ...assignment,
            isOptimal: true,
            optimalDaysMatched: trueCount,
        }));
        return {
            bestOption: getBestOption(optimalOptions, availability, preferredOrder),
        };
    }
    // Second attempt: Try all combinations with enhanced scoring
    const availabilityLength = availability.length;
    const requiredCoursesLength = requiredCourses.length;
    const allPossibleDays = Array.from({ length: availabilityLength }, (_, i) => i);
    // Use iterative combination generation (see function below)
    const combinations = getCombinations(allPossibleDays, requiredCoursesLength).sort((a, b) => getEnhancedCombinationScore(a, availabilityLength, optimumDays) -
        getEnhancedCombinationScore(b, availabilityLength, optimumDays));
    const allValidOptions = [];
    const optimumDaysIndices = optimumDays
        .map((isOptimal, index) => (isOptimal ? index : -1))
        .filter((index) => index !== -1);
    for (const dayIndices of combinations) {
        // Create a boolean map for the selected days
        const daysToTry = Array(availabilityLength).fill(false);
        for (const idx of dayIndices) {
            daysToTry[idx] = true;
        }
        const assignments = findAssignmentsForDays(requiredCourses, availability, daysToTry, false);
        const matchedOptimalDays = dayIndices.filter((idx) => optimumDaysIndices.includes(idx)).length;
        for (const assignmentOption of assignments) {
            allValidOptions.push({
                ...assignmentOption,
                isOptimal: false,
                optimalDaysMatched: matchedOptimalDays,
            });
        }
        // Limit to a reasonable number to reduce processing time
        if (allValidOptions.length >= 100)
            break;
    }
    if (allValidOptions.length === 0)
        return null;
    return {
        bestOption: getBestOption(allValidOptions, availability, preferredOrder),
    };
}
exports.findGolfAssignment = findGolfAssignment;
function getBestOption(options, overallAvailability, preferredOrder = [] // Add preferred order parameter
) {
    if (options.length === 0) {
        throw new Error("No options provided");
    }
    const overallFirstDate = dayjs(overallAvailability[0].date);
    const overallLastDate = dayjs(overallAvailability[overallAvailability.length - 1].date);
    // Group options by cost first
    const optionsByCost = new Map();
    for (const option of options) {
        if (!optionsByCost.has(option.totalCost)) {
            optionsByCost.set(option.totalCost, []);
        }
        optionsByCost.get(option.totalCost).push(option);
    }
    // Sort by cost (ascending)
    const costs = Array.from(optionsByCost.keys()).sort((a, b) => a - b);
    const lowestCostOptions = optionsByCost.get(costs[0]);
    // of all the lowest cost options sort so that the one that has the closest order to the preferred order is first (based on the options course_id)
    if (preferredOrder.length > 0) {
        lowestCostOptions.sort((a, b) => {
            const aOrderScore = calculatePreferredOrderScore(a.schedule, preferredOrder);
            const bOrderScore = calculatePreferredOrderScore(b.schedule, preferredOrder);
            return bOrderScore - aOrderScore;
        });
    }
    // Apply original criteria to lowest cost options
    let bestOption = lowestCostOptions[0];
    let bestUsesEdge = usesEdgeDays(bestOption.schedule, overallFirstDate, overallLastDate);
    let bestMiddleDays = countMiddleDays(bestOption.schedule);
    let bestGap = getTotalGap(bestOption.schedule);
    let bestOptimalDays = bestOption.optimalDaysMatched || 0;
    let bestOrderScore = preferredOrder.length > 0
        ? calculatePreferredOrderScore(bestOption.schedule, preferredOrder)
        : 0;
    for (let i = 1; i < lowestCostOptions.length; i++) {
        const currentOption = lowestCostOptions[i];
        const currentUsesEdge = usesEdgeDays(currentOption.schedule, overallFirstDate, overallLastDate);
        if (bestUsesEdge && !currentUsesEdge) {
            bestOption = currentOption;
            bestUsesEdge = currentUsesEdge;
            bestMiddleDays = countMiddleDays(currentOption.schedule);
            bestGap = getTotalGap(currentOption.schedule);
            bestOptimalDays = currentOption.optimalDaysMatched || 0;
            bestOrderScore =
                preferredOrder.length > 0
                    ? calculatePreferredOrderScore(currentOption.schedule, preferredOrder)
                    : 0;
            continue;
        }
        if (currentUsesEdge && !bestUsesEdge)
            continue;
        const currentMiddleDays = countMiddleDays(currentOption.schedule);
        if (currentMiddleDays > bestMiddleDays) {
            bestOption = currentOption;
            bestUsesEdge = currentUsesEdge;
            bestMiddleDays = currentMiddleDays;
            bestGap = getTotalGap(currentOption.schedule);
            bestOptimalDays = currentOption.optimalDaysMatched || 0;
            bestOrderScore =
                preferredOrder.length > 0
                    ? calculatePreferredOrderScore(currentOption.schedule, preferredOrder)
                    : 0;
            continue;
        }
        if (currentMiddleDays < bestMiddleDays)
            continue;
        const currentGap = getTotalGap(currentOption.schedule);
        if (currentGap > bestGap) {
            bestOption = currentOption;
            bestUsesEdge = currentUsesEdge;
            bestMiddleDays = currentMiddleDays;
            bestGap = currentGap;
            bestOptimalDays = currentOption.optimalDaysMatched || 0;
            bestOrderScore =
                preferredOrder.length > 0
                    ? calculatePreferredOrderScore(currentOption.schedule, preferredOrder)
                    : 0;
            continue;
        }
        if (currentGap < bestGap)
            continue;
        const currentOptimalDays = currentOption.optimalDaysMatched || 0;
        if (currentOptimalDays > bestOptimalDays) {
            bestOption = currentOption;
            bestUsesEdge = currentUsesEdge;
            bestMiddleDays = currentMiddleDays;
            bestGap = currentGap;
            bestOptimalDays = currentOptimalDays;
            bestOrderScore =
                preferredOrder.length > 0
                    ? calculatePreferredOrderScore(currentOption.schedule, preferredOrder)
                    : 0;
            continue;
        }
        if (currentOptimalDays < bestOptimalDays)
            continue;
        // Add preferred order as final tie-breaker when all other criteria are equal
        if (preferredOrder.length > 0) {
            const currentOrderScore = calculatePreferredOrderScore(currentOption.schedule, preferredOrder);
            if (currentOrderScore > bestOrderScore) {
                bestOption = currentOption;
                bestUsesEdge = currentUsesEdge;
                bestMiddleDays = currentMiddleDays;
                bestGap = currentGap;
                bestOptimalDays = currentOptimalDays;
                bestOrderScore = currentOrderScore;
            }
        }
    }
    return bestOption;
}
// Helper function to score how well a schedule matches preferred order
function calculatePreferredOrderScore(schedule, preferredOrder) {
    if (!preferredOrder.length)
        return 0;
    // Extract courses in chronological order
    const scheduledCourses = schedule
        .filter((day) => !!day)
        .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
        .map((day) => day.course_id);
    let score = 0;
    // Check for exact position matches
    for (let i = 0; i < Math.min(scheduledCourses.length, preferredOrder.length); i++) {
        if (scheduledCourses[i] === preferredOrder[i]) {
            // Award 10 points for exact position match
            score += 10;
        }
        else {
            // Award partial points for courses that exist in both lists but not at same position
            const preferredPos = preferredOrder.indexOf(scheduledCourses[i]);
            if (preferredPos !== -1) {
                // Award more points the closer the positions are
                const posDiff = Math.abs(preferredPos - i);
                // 9 points if off by 1 position, scaling down to 1 point if maximally distant
                score += Math.max(1, 10 - posDiff);
            }
        }
    }
    return score;
}
function usesEdgeDays(schedule, overallFirstDate, overallLastDate) {
    for (const day of schedule) {
        if (!day)
            continue;
        const date = dayjs(day.date);
        if (date.isSame(overallFirstDate, "day") ||
            date.isSame(overallLastDate, "day")) {
            return true;
        }
    }
    return false;
}
function countMiddleDays(schedule) {
    const validDates = [];
    let minDate = null;
    let maxDate = null;
    for (const day of schedule) {
        if (!day)
            continue;
        const date = dayjs(day.date);
        validDates.push(date);
        if (minDate === null || date.isBefore(minDate))
            minDate = date;
        if (maxDate === null || date.isAfter(maxDate))
            maxDate = date;
    }
    if (validDates.length === 0)
        return 0;
    const totalDays = maxDate.diff(minDate, "day") + 1;
    const middleStart = minDate.add(Math.floor(totalDays * 0.2), "day");
    const middleEnd = minDate.add(Math.ceil(totalDays * 0.8), "day");
    let count = 0;
    for (const date of validDates) {
        if (date.isAfter(middleStart, "day") && date.isBefore(middleEnd, "day")) {
            count++;
        }
    }
    return count;
}
function getTotalGap(schedule) {
    const validDates = schedule
        .filter((d) => !!d)
        .map((d) => dayjs(d.date));
    validDates.sort((a, b) => a.valueOf() - b.valueOf());
    let totalGap = 0;
    for (let i = 0; i < validDates.length - 1; i++) {
        totalGap += validDates[i + 1].diff(validDates[i], "day");
    }
    return totalGap;
}
function getEnhancedCombinationScore(combination, totalDays, optimumDays) {
    let score = 0;
    if (combination.includes(0))
        score += 10000;
    if (combination.includes(totalDays - 1))
        score += 10000;
    const middleStart = Math.floor(totalDays * 0.2);
    const middleEnd = Math.ceil(totalDays * 0.8);
    for (const dayIndex of combination) {
        if (dayIndex > middleStart && dayIndex < middleEnd) {
            score -= 2000;
        }
        if (optimumDays[dayIndex]) {
            score -= 100;
        }
    }
    if (combination.length > 1) {
        const idealGap = (totalDays - 1) / (combination.length - 1);
        for (let i = 0; i < combination.length - 1; i++) {
            const gap = combination[i + 1] - combination[i];
            if (gap < idealGap) {
                score += (idealGap - gap) * 1000;
            }
        }
    }
    return score;
}
/**
 * Iterative combination generator to avoid recursion.
 */
function getCombinations(arr, k) {
    const n = arr.length;
    if (k > n)
        return [];
    const indices = Array.from({ length: k }, (_, i) => i);
    const result = [];
    let shouldContinue = true;
    while (shouldContinue) {
        result.push(indices.map((i) => arr[i]));
        let i = k - 1;
        while (i >= 0 && indices[i] === i + n - k) {
            i--;
        }
        if (i < 0) {
            shouldContinue = false;
        }
        else {
            indices[i]++;
            for (let j = i + 1; j < k; j++) {
                indices[j] = indices[j - 1] + 1;
            }
        }
    }
    return result;
}
/**
 * Iterative backtracking to generate possible assignments.
 */
function generatePossibleAssignments(requiredCourses, selectedDays) {
    const availableCoursesPerDay = selectedDays.map((day) => day.courses
        .filter((course) => course.rate > 0 && course.rate_message === "RATES_FOUND")
        .map((course) => course.course_id));
    const result = [];
    const stack = [
        {
            dayIndex: 0,
            courseIndex: 0,
            remaining: [...requiredCourses],
            current: [],
        },
    ];
    while (stack.length > 0) {
        const top = stack[stack.length - 1];
        const { dayIndex, courseIndex, remaining, current } = top;
        if (dayIndex === availableCoursesPerDay.length) {
            if (remaining.length === 0) {
                result.push([...current]);
            }
            stack.pop();
            if (stack.length > 0) {
                stack[stack.length - 1].courseIndex++;
            }
            continue;
        }
        if (courseIndex >= availableCoursesPerDay[dayIndex].length) {
            stack.pop();
            if (stack.length > 0) {
                stack[stack.length - 1].courseIndex++;
            }
            continue;
        }
        const course = availableCoursesPerDay[dayIndex][courseIndex];
        const idx = remaining.indexOf(course);
        if (idx !== -1) {
            const newRemaining = [
                ...remaining.slice(0, idx),
                ...remaining.slice(idx + 1),
            ];
            stack.push({
                dayIndex: dayIndex + 1,
                courseIndex: 0,
                remaining: newRemaining,
                current: [...current, course],
            });
        }
        else {
            top.courseIndex++;
        }
    }
    return result;
}
function calculateTotalCost(courseAssignment, selectedDays) {
    let totalCost = 0;
    for (let i = 0; i < courseAssignment.length; i++) {
        const course = selectedDays[i].courses.find((c) => c.course_id === courseAssignment[i]);
        if (!course || course.rate === 0 || course.rate_message !== "RATES_FOUND") {
            return Infinity;
        }
        totalCost += course.rate;
    }
    return totalCost;
}
function findAssignmentsForDays(requiredCourses, availability, daysToTry, isOptimalAttempt) {
    // Gather selected days with their indices.
    const selectedDaysWithIndices = availability.reduce((acc, day, index) => {
        if (daysToTry[index])
            acc.push({ day, index });
        return acc;
    }, []);
    if (selectedDaysWithIndices.length !== requiredCourses.length)
        return [];
    const selectedDays = selectedDaysWithIndices.map((item) => item.day);
    const possibleAssignments = generatePossibleAssignments(requiredCourses, selectedDays);
    const validAssignments = [];
    for (const assignment of possibleAssignments) {
        const totalCost = calculateTotalCost(assignment, selectedDays);
        if (totalCost < Infinity) {
            const schedule = new Array(availability.length);
            for (let i = 0; i < selectedDaysWithIndices.length; i++) {
                const { day, index } = selectedDaysWithIndices[i];
                const courseId = assignment[i];
                const courseInfo = day.courses.find((c) => c.course_id === courseId);
                schedule[index] = {
                    date: day.date,
                    course_id: courseId,
                    config: courseInfo.config,
                    rate_message: courseInfo.rate_message,
                    cost: courseInfo.rate,
                };
            }
            validAssignments.push({
                schedule,
                totalCost,
                isOptimal: isOptimalAttempt,
            });
        }
    }
    return validAssignments;
}
